// Footer toolbar under SimCanvas: zoom controls for the canvas
// itself, plus a magnifying-glass overlay that follows the cursor.
// The magnifier is pure presentation today — it samples the same
// WebGL-rendered grid — but the goal is for it to eventually
// surface particle-level detail streamed from the worker server,
// so the API surface (active toggle, zoom factor) is sized for
// that future use without leaking it into the current callers.

export interface FooterProps {
  /** Current canvas pixel scale (0.25..6). Each cell is rendered as
   * `pixelScale × pixelScale` CSS pixels. Sub-1 values let a 2048
   * world fit a laptop viewport (0.25x = 512 px wide). */
  pixelScale: number;
  /** Step the canvas zoom up / down one rung along the shared
   * `ZOOM_STEPS` ladder. */
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Whether the magnifier overlay is currently following the cursor. */
  magnifierActive: boolean;
  onToggleMagnifier: () => void;
  /** Linear zoom factor of the magnifier overlay (1.5..16). */
  magnifierZoom: number;
  onMagnifierZoom: (next: number) => void;
  /** Whether the zoom-on-click tool is active. Click zooms one rung
   * in centred on the clicked cell; shift-click zooms one rung out
   * with the same anchor. */
  zoomToolActive: boolean;
  onToggleZoomTool: () => void;
}

/** Format pixelScale for the readout. Sub-1 values show one decimal
 * (0.25x, 0.5x); integer values stay clean (2x, not 2.0x). */
function formatZoom(scale: number): string {
  return scale < 1 || !Number.isInteger(scale)
    ? `${scale.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")}×`
    : `${scale.toFixed(0)}×`;
}

export function Footer(props: FooterProps) {
  return (
    <footer className="canvas-footer">
      <div className="canvas-footer-group">
        <button
          type="button"
          className="toolbar-button"
          onClick={props.onZoomOut}
          title="Zoom canvas out"
          aria-label="Zoom out"
        >
          −
        </button>
        <span className="canvas-footer-readout">{formatZoom(props.pixelScale)}</span>
        <button
          type="button"
          className="toolbar-button"
          onClick={props.onZoomIn}
          title="Zoom canvas in"
          aria-label="Zoom in"
        >
          +
        </button>
      </div>

      <div className="canvas-footer-divider" aria-hidden="true" />

      <div className="canvas-footer-group">
        <button
          type="button"
          className={"toolbar-button" + (props.magnifierActive ? " active" : "")}
          onClick={props.onToggleMagnifier}
          title="Toggle magnifying-glass overlay"
          aria-label="Toggle magnifier"
          aria-pressed={props.magnifierActive}
        >
          <span className="toolbar-icon" aria-hidden="true">🔍</span>
          <span className="toolbar-label">Magnifier</span>
        </button>
        <label className="canvas-footer-slider" title="Magnifier zoom level">
          <span className="canvas-footer-readout">
            {props.magnifierZoom.toFixed(1)}×
          </span>
          <input
            type="range"
            min={1.5}
            max={16}
            step={0.5}
            value={props.magnifierZoom}
            onChange={(e) => props.onMagnifierZoom(Number(e.target.value))}
            disabled={!props.magnifierActive}
          />
        </label>
        <button
          type="button"
          className={"toolbar-button" + (props.zoomToolActive ? " active" : "")}
          onClick={props.onToggleZoomTool}
          title="Zoom-on-click: click a cell to zoom in (shift-click to zoom out), anchored on the clicked point"
          aria-label="Toggle zoom-on-click tool"
          aria-pressed={props.zoomToolActive}
        >
          <span className="toolbar-icon" aria-hidden="true">🔎</span>
          <span className="toolbar-label">Zoom</span>
        </button>
      </div>
    </footer>
  );
}
